/**
 * Department Form Component
 * 
 * Form component for creating and editing departments using OForm.
 * Handles form validation, submission, and error handling.
 * 
 * @component
 */

import React, { useMemo } from 'react';
import * as yup from 'yup';
import { Box } from '@mui/material';
import OForm from '../common/OForm';
import { isValidUSPhoneNumber } from '../../utils/helpers/format-phone-number';

const DepartmentForm = ({ 
  department, 
  onClose, 
  setRenderType, 
  renderType, 
  facilities, 
  handleSubmit 
}) => {


  // Validation schema
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required('Department name is required')
      .min(1, 'Department name must be at least 1 character')
      .max(100, 'Department name must not exceed 100 characters')
      .trim(),
    facilityId: yup
      .string()
      .required('Facility selection is required'),
    faxNumber: yup
      .string()
      .nullable()
      .optional()
      .test('valid-fax', 'Enter a valid US fax number', (value) => {
        if (!value || value.trim() === '') return true; // Optional field
        return isValidUSPhoneNumber(value);
      }),
    isActive: yup.boolean()
  });

  // Keep initialValues stable across re-renders to prevent OForm reset
  const initialFormValues = useMemo(() => (
    department || {
      name: '',
      facilityId: '',
      faxNumber: '',
      isActive: true,
    }
  ), [department]);

  const getFields = () => [
    {
      fieldName: 'name',
      label: 'Department Name*',
      placeholder: 'Department Name',
      renderType: 'text',
      isRequired: true,
    },
    {
      fieldName: 'facilityId',
      label: 'Facility*',
      placeholder: 'Select a facility',
      renderType: 'select',
      isRequired: true,
      disabled: department ? true : false,
      options: facilities.map(facility => ({
        label: `${facility.facilityName} - (${facility.location.streetAddress})`,
        value: facility._id,
      })),
    },
    {
      fieldName: 'faxNumber',
      label: 'Fax No.',
      placeholder: 'Fax No.',
      renderType: 'phoneNumber',
      isRequired: false,
    },
    // Note: isActive is managed via status toggle in the list, not in forms
  ];

  return (
    <Box>
      <OForm
        formTitle={department ? 'Edit Department' : 'Add New Department'}
        initialValues={initialFormValues}
        fields={getFields}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        handleBack={onClose}
        submitButtonText={department ? 'Update' : 'Create'}
        backButtonText="Cancel"
        fieldsPerRow={2}
        headerButtonLabel="Back"
        headerButtonOnClick={() => setRenderType(renderType === 'list' ? 'form' : 'list')}
      />
    </Box>
  );
};

export default DepartmentForm;
